import React, { useState, useEffect, memo } from 'react';
import _ from 'lodash';
import { moment } from 'i18n';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { Copy, Transaction } from 'grommet-icons';

import { nestedTranslate, handleCopyToClipboard } from 'utils';
import { Box, ButtonNew, Paragraph } from 'components/Wrapped';
import { TableWrapper } from 'containers/Tables';
import { ExternalLink } from 'components/Helpers';
import { formatNumberToPlaces } from 'utils/numbers';

import { withRouter } from 'react-router';
import { getWithdrawalHistory, getDepositHistory } from 'redux/actions/portfolio';
import { authenticatedInstance } from 'api';

import { triggerToast } from 'redux/actions/ui';

const WalletHistoryTable = memo((props) => {
  const { t: translate, getWithdrawalHistory, getDepositHistory, type, currency, depositHistory, withdrawalHistory } = props;
  const t = nestedTranslate(translate, 'tables.withdrawalHistory');
  const [walletHistory, setWalletHistory] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const [loadingItemId, setLoadingItemId] = useState(null);

  const isFiatWithdrawal = type === 'withdrawal' && currency === 'RUB';
  const isFiatDeposit = type === 'deposit' && currency === 'RUB';
  const isFiatAll = type === 'all' && currency === 'RUB';
  const isCryptoWithdrawal = type === 'withdrawal' && currency === 'USDT';
  const isCryptoDeposit = type === 'deposit' && currency === 'USDT';
  const isCryptoAll = type === 'all' && currency === 'USDT';

  useEffect(() => {
    if (isCryptoWithdrawal || isFiatWithdrawal || isFiatAll || isCryptoAll) {
      getWithdrawalHistory(currency);
    }

    if (isCryptoDeposit || isCryptoAll) {
      getDepositHistory(currency, 'Crypto');
    }

    if (isFiatDeposit || isFiatAll) {
      getDepositHistory(currency, 'Fiat');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, type, updateTrigger]);

  useEffect(() => {
    if (isCryptoWithdrawal || isFiatWithdrawal) {
      setWalletHistory(withdrawalHistory);
    }

    if (isCryptoDeposit || isFiatDeposit) {
      setWalletHistory(depositHistory);
    }

    if (isCryptoAll || isFiatAll) {
      setWalletHistory([...depositHistory, ...withdrawalHistory]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawalHistory, depositHistory, currency, type]);

  const handleCancel = async (data) => {
    if (data?.status === 'New') {
      const { id } = data;
      setLoadingItemId(id);
      try {
        const data = await authenticatedInstance({
          url: `fiat/cancelDepositRequest/${id}`,
          method: 'POST',
        });

        if (!data) {
          triggerToast('Произошла ошибка при отмене заявки, пожалуйста, обратитесь в поддержку', 'error', 2500);
        } else {
          triggerToast('Заявка успешно отменена', 'success', 2500);
          setUpdateTrigger(!updateTrigger);
        }
      } catch (e) {
        triggerToast('Произошла ошибка при отмене заявки, пожалуйста, обратитесь в поддержку', 'error', 2500);
      } finally {
        setLoadingItemId(null);
      }
      return;
    }

    if (data?.withdrawalStatus === 'Pending') {
      const { id } = data;
      setLoadingItemId(id);
      try {
        const data = await authenticatedInstance({
          url: `fiat/cancelWithdrawalRequest/${id}`,
          method: 'POST',
        });

        if (!data) {
          triggerToast('Произошла ошибка при отмене заявки, пожалуйста, обратитесь в поддержку', 'error', 2500);
        } else {
          triggerToast('Заявка успешно отменена', 'success', 2500);
          setUpdateTrigger(!updateTrigger);
        }
      } catch (e) {
        triggerToast('Произошла ошибка при отмене заявки, пожалуйста, обратитесь в поддержку', 'error', 2500);
      } finally {
        setLoadingItemId(null);
      }
      return;
    }
  };

  const sortWalletHistoryInOrder = () => {
    return walletHistory.sort((a, b) => {
      const dateA = a.depositReqDate ?? a.depositConfirmDate ?? a.approvedDate ?? a.requestDate ?? a.withdrawalReqDate;
      const dateB = b.depositReqDate ?? b.depositConfirmDate ?? b.approvedDate ?? b.requestDate ?? b.withdrawalReqDate;

      return new Date(dateB) - new Date(dateA);
    });
  };

  if (_.isEmpty(walletHistory)) {
    return (
      <Box align="center" gap="small">
        <Transaction size="50px" />
        <Paragraph>{t('noWithdrawalHistory')}</Paragraph>
      </Box>
    );
  }

  return (
    <TableWrapper
      data={sortWalletHistoryInOrder(walletHistory)}
      isFilterable={false}
      fileName="withdrawal-history"
      columns={[
        {
          Header: 'Номер',
          id: 'id',
          accessor: ({ id }) => id || '-',
        },
        {
          Header: 'Тип',
          id: 'type',
          accessor: ({ withdrawalAddress }) => (withdrawalAddress ? 'Вывод средств' : 'Пополнение средств'),
        },
        {
          Header: 'Метод',
          id: 'method',
          accessor: ({ type }) => (type === 'Bank' ? 'Наличные' : 'Криптовалюта'),
        },
        {
          Header: 'Сеть',
          id: 'network',
          accessor: ({ network }) => network || '-',
        },
        {
          Header: 'Детали',
          id: 'details',
          accessor: ({ txnHash, withdrawalAddress }) => txnHash,
          Cell: ({ value, original: { explorerURL } }) =>
            value ? (
              <Box pad="none" direction="row">
                <ExternalLink
                  style={{
                    maxWidth: 75,
                    marginRight: 10,
                    color: 'white',
                  }}
                  href={explorerURL}
                >
                  <div
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {value}
                  </div>
                </ExternalLink>
                <Copy size="small" onClick={() => handleCopyToClipboard(value)} style={{ cursor: 'pointer' }} />
              </Box>
            ) : (
              '-'
            ),
        },
        {
          Header: 'Сумма',
          id: 'amount',
          accessor: ({ depositAmount, requestAmount, withdrawalAmount }) =>
            depositAmount
              ? `${formatNumberToPlaces(depositAmount)}`
              : requestAmount
              ? `${formatNumberToPlaces(requestAmount)}`
              : `${formatNumberToPlaces(withdrawalAmount)}`,
        },
        {
          Header: 'Дата',
          id: 'date',
          accessor: (data) =>
            moment
              .utc(
                data?.depositReqDate ??
                  data?.depositConfirmDate ??
                  data?.approvedDate ??
                  data?.requestDate ??
                  data?.withdrawalReqDate ??
                  '',
              )
              .local()
              .format('DD.MM.YYYY HH:mm'),
        },
        {
          Header: 'Статус',
          id: 'status',
          accessor: ({ depositConfirmDate, status, withdrawalStatus }) =>
            status === 'Approved' || depositConfirmDate
              ? 'Завершена'
              : status === 'Cancelled' || withdrawalStatus === 'Cancelled'
              ? 'Отклонена'
              : 'На проверке',
        },
        {
          Header: 'Действие',
          id: 'action',
          accessor: (data) =>
            data.type === 'Bank' && (data.status === 'New' || data.withdrawalStatus === 'Pending') ? (
              <ButtonNew
                onClick={() => handleCancel(data)}
                isLoading={loadingItemId === data.id}
                label={
                  <Box pad="5px 10px" width="fit-content">
                    {'Отменить'}
                  </Box>
                }
                theme="gray"
                width={'fit-content'}
              />
            ) : (
              '-'
            ),
        },
      ]}
      sortable={false}
      showPagination={true}
      minRows={10}
      pageSize={10}
      background="transparent"
      headBackground="transparent"
      sortShadowColor="transparent"
    />
  );
});

const mapStateToProps = ({ portfolio, ui }, ownProps) => ({
  withdrawalHistory: portfolio.withdrawalHistory,
  depositHistory: portfolio.depositHistory,
  isModalOpen: ui.isModalOpen,
  type: ownProps.type,
  currency: ownProps.currency,
});

export default withRouter(withNamespaces()(connect(mapStateToProps, { getWithdrawalHistory, getDepositHistory })(WalletHistoryTable)));
